import * as React from "react";
import { cn } from "@/lib/utils";
import { SpotlightCard, SpotlightCardContent } from "./SpotlightCard";
import { GradientText, SectionLabel } from "./GradientText";
import {
  Users,
  Zap,
  BarChart3,
  Link2,
  Shield,
  Headphones,
  Globe,
  type LucideIcon,
} from "lucide-react";

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface FeaturesProps {
  features: Feature[];
  heading?: string;
  description?: string[];
}

const iconMap: Record<string, LucideIcon> = {
  users: Users,
  zap: Zap,
  chart: BarChart3,
  link: Link2,
  shield: Shield,
  headphones: Headphones,
  globe: Globe,
};

export function Features({ features, heading, description }: FeaturesProps) {
  const [isVisible, setIsVisible] = React.useState(false);
  const sectionRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Define bento grid layout patterns
  const getGridClasses = (index: number) => {
    const patterns = [
      "md:col-span-2 md:row-span-2", // Hero card - large
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-2", // Tall card
      "md:col-span-2 md:row-span-1", // Wide card
      "md:col-span-1 md:row-span-1", // Standard
      "md:col-span-1 md:row-span-1", // Standard
    ];
    return patterns[index % patterns.length];
  };

  return (
    <section ref={sectionRef} id="features" className="py-24 lg:py-32">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div
          className={cn(
            "text-center max-w-3xl mx-auto mb-16 lg:mb-20",
            "transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
        >
          <SectionLabel className="mb-6">Features</SectionLabel>

          {heading && (
            <h2
              className={cn(
                "text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight",
                "mb-6"
              )}
            >
              <GradientText>{heading}</GradientText>
            </h2>
          )}

          {description?.map((paragraph, i) => (
            <p
              key={i}
              className="text-muted-foreground text-base lg:text-lg leading-relaxed mb-4 last:mb-0"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Bento grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-6 auto-rows-[180px]">
          {features.map((feature, index) => {
            const Icon = iconMap[feature.icon] || Zap;
            const isHeroCard = index === 0;

            return (
              <SpotlightCard
                key={feature.id}
                className={cn(
                  getGridClasses(index),
                  "transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]",
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                )}
                style={{
                  transitionDelay: isVisible ? `${index * 80}ms` : "0ms",
                }}
              >
                <SpotlightCardContent
                  className={cn(
                    "h-full flex flex-col overflow-hidden",
                    isHeroCard ? "p-6 lg:p-8" : "p-5"
                  )}
                >
                  {/* Icon + Title row */}
                  <div className="flex items-center gap-3 mb-3 min-w-0">
                    {/* Icon */}
                    <div
                      className={cn(
                        "w-10 h-10 rounded-xl flex items-center justify-center shrink-0",
                        "bg-accent/10",
                        "border border-accent/20"
                      )}
                    >
                      <Icon className="w-5 h-5 text-accent" />
                    </div>

                    {/* Title */}
                    <h3
                      className={cn(
                        "font-semibold text-foreground truncate",
                        isHeroCard ? "text-lg lg:text-xl" : "text-base"
                      )}
                      title={feature.title}
                    >
                      {feature.title}
                    </h3>
                  </div>

                  {/* Description */}
                  <p
                    className={cn(
                      "text-muted-foreground leading-relaxed flex-1 line-clamp-3",
                      isHeroCard
                        ? "text-sm lg:text-base line-clamp-5"
                        : "text-sm"
                    )}
                  >
                    {feature.description}
                  </p>
                </SpotlightCardContent>
              </SpotlightCard>
            );
          })}
        </div>
      </div>
    </section>
  );
}
